/* eslint-disable camelcase -- GA4 parameter names are snake_case */
import moment from 'moment-timezone';
import analytics from '../../infra/analytics';
import type AvailableTime from '../../model/AvailableTime';
import { expandTimeslotsForDay } from '../../utils/expand-timeslots';
import { ADHD_BASE, ADHD_BOOKING_PATH, EVENT_NAMES } from '../data/constants';
import type { SubmissionFailure } from '../hooks/submission-state';
import type { TimeSegment } from './booking-slots';

// Every parameter reads in GA4 without a lookup table: words, concrete dates
// and months, or raw counts and seconds that reports can group themselves;
// never relative values or bands (see docs/analytics/adhd-booking-events.md).
// The same concept keeps the same name and type on every event.
// Dates and times are in the patient's timezone, as the page shows them, and
// each event that carries one also sends that timezone. No patient details or
// ids are sent; the clinician's display name is staff information.

const DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_FORMAT = 'YYYYMM';
const TIME_FORMAT = 'HH:mm';
const SLOT_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

/** YYYY-MM-DD; none when there is no free slot; loading while availability is still being fetched. */
export type DateParam = string;

/** A soonest slot, or 'loading' when not every month that could hold it has been fetched yet. */
export type SoonestSlot = moment.Moment | null | 'loading';

/** Calendar month as YYYY-MM. */
export type MonthParam = string;

export type YesNo = 'yes' | 'no';

export type BookingErrorType = 'slot_taken' | 'network' | 'validation' | 'server' | 'other';

export type MonthDirection = 'previous' | 'next';

export type SelectionLostReason = 'slot_expired' | 'date_unavailable';

export type BookingStage =
  | 'choosing_clinician'
  | 'loading_slots'
  | 'choosing_date'
  | 'choosing_time'
  | 'ready_to_confirm'
  | 'submitting'
  | 'error';

export type FeeStatus = 'shown' | 'loading' | 'unavailable' | 'missing';

export type ExitTrigger = 'hidden' | 'pagehide' | 'back';

export interface SlotsLoadedParams {
  clinician_name: string
  month: MonthParam
  /** Date of the clinician's soonest slot in any loaded month. */
  next_available: DateParam
  /** Days in the month that still have a bookable slot. */
  available_days: number
  timezone: string
}

export interface ClinicianSelectedParams {
  clinician_name: string
  next_available: DateParam
  /** Date of the soonest slot across every clinician, to compare with next_available. */
  soonest_any_clinician: DateParam
  timezone: string
}

/** The date, and time once picked, the patient chose. */
export interface SelectionParams {
  clinician_name: string
  selected_date: DateParam
  /** HH:mm, only once a time is picked. */
  selected_time?: string
  timezone: string
}

/** Seconds are GA4 custom metrics, so reports can average them. */
export interface ConfirmParams extends SelectionParams {
  /** From opening the booking step to this event. */
  seconds_on_step: number
  /** From picking the time to this event; left out when the time was restored, not picked. */
  seconds_since_slot_selected?: number
}

export interface ConfirmErrorParams extends ConfirmParams {
  error_type: BookingErrorType
}

/** Where the patient was on the step when they left it. */
export interface StepSnapshotParams {
  stage: BookingStage
  /** Failure kind shown on the page, e.g. appointment_conflict. */
  last_error?: string
  fee_status: FeeStatus
  /** Seconds from opening the booking step, as on the confirm events. */
  seconds_on_step: number
  browsed_other_months: YesNo
  /** Only once a clinician is chosen. */
  clinician_name?: string
  next_available?: DateParam
  timezone?: string
}

/** How the confirm request ended at the HTTP level, when it got that far. */
export interface ConfirmTransport {
  httpStatus?: number
  networkError?: boolean
}

/** When the patient opened the booking step and picked the time (Date.now() values). */
export interface BookingTiming {
  enteredAt: number
  /** null until a time is picked on this visit. */
  slotSelectedAt: number | null
}

/** The booking being confirmed, as BookingValues carries it, plus its timing. */
export interface ConfirmContext {
  clinicianName: string
  /** ISO date-time with offset, e.g. 2026-10-06T09:00+11:00. */
  startDateTime: string
  timing: BookingTiming
}

interface SlotsLoadedInput {
  clinicianName: string
  month: string
  nextAvailable: SoonestSlot
  days: AvailableTime[]
  appointmentLengthMinutes: number
  now: moment.Moment
  timezone: string
}

export interface StageInput {
  clinicianChosen: boolean
  slotsLoading: boolean
  dateChosen: boolean
  bookable: boolean
  submitting: boolean
  failure: SubmissionFailure | null
}

export interface FeeStatusInput {
  hasEstimate: boolean
  loading: boolean
  unavailable: boolean
}

interface StepSnapshotInput {
  stage: BookingStage
  failure: SubmissionFailure | null
  feeStatus: FeeStatus
  browsedOtherMonths: boolean
  msOnStep: number
  /** Empty until a clinician is chosen. */
  clinicianName: string
  nextAvailable: SoonestSlot
  timezone: string
}

/** The timezone slots are fetched and shown in (the availabilities API defaults to it). */
export const browserTimezone = (): string => moment.tz.guess();

export const toDateParam = (slot: SoonestSlot): DateParam => {
  if (slot === 'loading') return 'loading';
  return slot ? slot.format(DATE_FORMAT) : 'none';
};

/** YYYYMM, as the calendar keys months, to YYYY-MM. */
export const toMonthParam = (month: string): MonthParam => moment(month, MONTH_FORMAT).format('YYYY-MM');

export const countAvailableDays = (
  days: AvailableTime[],
  appointmentLengthMinutes: number,
  now: moment.Moment,
): number => days.filter((day) => (
  expandTimeslotsForDay(day.date, day.availableTimeslots, appointmentLengthMinutes)
    .some((slot) => moment(`${day.date} ${slot.start}`, SLOT_DATE_TIME_FORMAT).isAfter(now))
)).length;

const toSeconds = (ms: number): number => Math.round(ms / 1000);

export const buildSlotsLoadedParams = ({
  clinicianName, month, nextAvailable, days, appointmentLengthMinutes, now, timezone,
}: SlotsLoadedInput): SlotsLoadedParams => ({
  clinician_name: clinicianName,
  month: toMonthParam(month),
  next_available: toDateParam(nextAvailable),
  available_days: countAvailableDays(days, appointmentLengthMinutes, now),
  timezone,
});

export const buildClinicianSelectedParams = (
  clinicianName: string,
  nextAvailable: SoonestSlot,
  soonestAnyClinician: SoonestSlot,
  timezone: string,
): ClinicianSelectedParams => ({
  clinician_name: clinicianName,
  next_available: toDateParam(nextAvailable),
  soonest_any_clinician: toDateParam(soonestAnyClinician),
  timezone,
});

export const buildSelectionParams = (
  clinicianName: string,
  selected: moment.Moment,
  withTime: boolean,
  timezone: string,
): SelectionParams => ({
  clinician_name: clinicianName,
  selected_date: toDateParam(selected),
  ...(withTime ? { selected_time: selected.format(TIME_FORMAT) } : {}),
  timezone,
});

const errorTypeFromTransport = ({ httpStatus, networkError }: ConfirmTransport): BookingErrorType => {
  if (networkError) return 'network';
  if (httpStatus === 400 || httpStatus === 422) return 'validation';
  if (httpStatus !== undefined && httpStatus >= 500) return 'server';
  return 'other';
};

export const toBookingErrorType = (
  failure: SubmissionFailure,
  transport: ConfirmTransport = {},
): BookingErrorType => {
  switch (failure.kind) {
    case 'appointment-conflict':
    case 'slot-expired':
      return 'slot_taken';
    case 'validation':
      return 'validation';
    case 'unknown':
      return errorTypeFromTransport(transport);
    default:
      return 'other';
  }
};

export const deriveBookingStage = ({
  clinicianChosen, slotsLoading, dateChosen, bookable, submitting, failure,
}: StageInput): BookingStage => {
  if (submitting) return 'submitting';
  if (failure) return 'error';
  if (!clinicianChosen) return 'choosing_clinician';
  if (slotsLoading) return 'loading_slots';
  if (!dateChosen) return 'choosing_date';
  if (!bookable) return 'choosing_time';
  return 'ready_to_confirm';
};

export const deriveFeeStatus = ({ hasEstimate, loading, unavailable }: FeeStatusInput): FeeStatus => {
  if (hasEstimate) return 'shown';
  if (unavailable) return 'unavailable';
  if (loading) return 'loading';
  return 'missing';
};

export const buildStepSnapshotParams = ({
  stage, failure, feeStatus, browsedOtherMonths, msOnStep, clinicianName, nextAvailable, timezone,
}: StepSnapshotInput): StepSnapshotParams => ({
  stage,
  ...(failure ? { last_error: failure.kind.replace(/-/g, '_') } : {}),
  fee_status: feeStatus,
  seconds_on_step: toSeconds(msOnStep),
  browsed_other_months: browsedOtherMonths ? 'yes' : 'no',
  ...(clinicianName
    ? { clinician_name: clinicianName, next_available: toDateParam(nextAvailable), timezone }
    : {}),
});

const secondsSince = (at: number): number => toSeconds(Date.now() - at);

const confirmParams = ({ clinicianName, startDateTime, timing }: ConfirmContext): ConfirmParams => ({
  ...buildSelectionParams(clinicianName, moment(startDateTime), true, browserTimezone()),
  seconds_on_step: secondsSince(timing.enteredAt),
  ...(timing.slotSelectedAt === null ? {} : { seconds_since_slot_selected: secondsSince(timing.slotSelectedAt) }),
});

/**
 * Every booking event carries page_path, as the step page views do. It is the
 * booking step's own path, not the current URL: an exit can be sent after the
 * router has already moved to the next route.
 */
const BOOKING_PAGE_PATH = `${ADHD_BASE}${ADHD_BOOKING_PATH}`;

const logBookingEvent = (name: string, params: object = {}): void => {
  analytics.logEvent(name, { page_path: BOOKING_PAGE_PATH, ...params });
};

export const trackSlotsLoaded = (params: SlotsLoadedParams): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_SLOTS_LOADED, params);
};

export const trackSlotsLoadFailed = (clinicianName: string, month: string): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_SLOTS_LOAD_FAILED, {
    clinician_name: clinicianName,
    month: toMonthParam(month),
  });
};

export const trackClinicianSelected = (params: ClinicianSelectedParams): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_CLINICIAN_SELECTED, params);
};

export const trackMonthChanged = (direction: MonthDirection, targetMonth: string): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_MONTH_CHANGED, {
    direction,
    month: toMonthParam(targetMonth),
  });
};

export const trackDateSelected = (clinicianName: string, slotDate: string): void => {
  logBookingEvent(
    EVENT_NAMES.BOOKING_DATE_SELECTED,
    buildSelectionParams(clinicianName, moment(slotDate, DATE_FORMAT), false, browserTimezone()),
  );
};

export const trackTimeSegmentSelected = (segment: TimeSegment, clinicianName: string): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_TIME_SEGMENT_SELECTED, { segment, clinician_name: clinicianName });
};

export const trackSlotSelected = (clinicianName: string, slotDate: string, slotTime: string): void => {
  logBookingEvent(
    EVENT_NAMES.BOOKING_SLOT_SELECTED,
    buildSelectionParams(
      clinicianName,
      moment(`${slotDate} ${slotTime}`, SLOT_DATE_TIME_FORMAT),
      true,
      browserTimezone(),
    ),
  );
};

export const trackSelectionLost = (reason: SelectionLostReason): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_SELECTION_LOST, { reason });
};

export const trackFeeDetailsOpen = (): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_FEE_DETAILS_OPEN);
};

export const trackFeeUnavailable = (): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_FEE_UNAVAILABLE);
};

export const trackFeeRetry = (): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_FEE_RETRY);
};

export const trackConfirmClick = (confirm: ConfirmContext): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_CONFIRM_CLICK, confirmParams(confirm));
};

export const trackConfirmSuccess = (confirm: ConfirmContext): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_CONFIRM_SUCCESS, confirmParams(confirm));
};

export const trackConfirmError = (
  confirm: ConfirmContext,
  failure: SubmissionFailure,
  transport?: ConfirmTransport,
): void => {
  const params: ConfirmErrorParams = {
    ...confirmParams(confirm),
    error_type: toBookingErrorType(failure, transport),
  };
  logBookingEvent(EVENT_NAMES.BOOKING_CONFIRM_ERROR, params);
};

export const trackBackClick = (): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_BACK_CLICK);
};

export const trackStepExit = (snapshot: StepSnapshotParams, trigger: ExitTrigger): void => {
  logBookingEvent(EVENT_NAMES.BOOKING_STEP_EXIT, { ...snapshot, trigger });
};
